import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosConfig";
import { useLookup } from "../../utils/useLookup";
import { formatReportDate } from "../../utils/reportDate";

const ShipmentScheduleReport = () => {
  const { suppliers: shippers, buyers } = useLookup();
  const [filters, setFilters] = useState({
    fromDate: "", toDate: "",
    shipperId: "", buyerId: "",
    dealId: "", containerNo: "",
    blNo: "", invoiceNo: ""
  });
  const [rows, setRows] = useState([]);
  const [generated, setGenerated] = useState(false);

  const fmt = formatReportDate;

  const handleGenerate = async () => {
    try {
      const body = {};
      if (filters.fromDate)   body.fromDate    = filters.fromDate;
      if (filters.toDate)     body.toDate      = filters.toDate;
      if (filters.shipperId)  body.shipperId   = Number(filters.shipperId);
      if (filters.buyerId)    body.buyerId     = Number(filters.buyerId);
      if (filters.dealId)     body.dealId      = Number(filters.dealId);
      if (filters.containerNo) body.containerNo = filters.containerNo;
      if (filters.blNo)       body.blNo        = filters.blNo;
      if (filters.invoiceNo)  body.invoiceNo   = filters.invoiceNo;

      const res = await axios.post("/shipment/report/search", body);
      setRows(res.data);
      setGenerated(true);
    } catch (err) {
      alert("Failed to generate report.");
    }
  };

  const handleClear = () => {
    setFilters({ fromDate: "", toDate: "", shipperId: "", buyerId: "", dealId: "", containerNo: "", blNo: "", invoiceNo: "" });
    setRows([]);
    setGenerated(false);
  };

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  return (
    <div className="container-fluid mt-3">
      <div className="card shadow-sm border-0">

        {/* Header */}
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <span className="badge bg-success fs-6 px-3 py-2">Shipment Schedule Report</span>
        </div>

        <div className="card-body">

          {/* Filter Panel */}
          <div className="row g-3 mb-3">

            <div className="col-md-2">
              <label className="form-label">From Date</label>
              <input type="date" className="form-control form-control-sm"
                value={filters.fromDate} onChange={e => set("fromDate", e.target.value)} />
            </div>

            <div className="col-md-2">
              <label className="form-label">To Date</label>
              <input type="date" className="form-control form-control-sm"
                value={filters.toDate} onChange={e => set("toDate", e.target.value)} />
            </div>

            <div className="col-md-2">
              <label className="form-label">Supplier</label>
              <select className="form-select form-select-sm"
                value={filters.shipperId} onChange={e => set("shipperId", e.target.value)}>
                <option value="">All</option>
                {shippers.map(s => <option key={s.ShipperID} value={s.ShipperID}>{s.ShipperName}</option>)}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Buyer</label>
              <select className="form-select form-select-sm"
                value={filters.buyerId} onChange={e => set("buyerId", e.target.value)}>
                <option value="">All</option>
                {buyers.map(b => <option key={b.BuyerID} value={b.BuyerID}>{b.BuyerName}</option>)}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Deal ID</label>
              <input type="number" className="form-control form-control-sm"
                value={filters.dealId} onChange={e => set("dealId", e.target.value)} placeholder="Deal ID" />
            </div>

            <div className="col-md-2">
              <label className="form-label">Container No</label>
              <input type="text" className="form-control form-control-sm"
                value={filters.containerNo} onChange={e => set("containerNo", e.target.value)} placeholder="Container No" />
            </div>

            <div className="col-md-2">
              <label className="form-label">BL No</label>
              <input type="text" className="form-control form-control-sm"
                value={filters.blNo} onChange={e => set("blNo", e.target.value)} placeholder="BL No" />
            </div>

            <div className="col-md-2">
              <label className="form-label">Invoice No</label>
              <input type="text" className="form-control form-control-sm"
                value={filters.invoiceNo} onChange={e => set("invoiceNo", e.target.value)} placeholder="Invoice No" />
            </div>

            <div className="col-md-12 d-flex gap-2">
              <button className="btn btn-success btn-sm" onClick={handleGenerate}>Generate Report</button>
              <button className="btn btn-secondary btn-sm" onClick={handleClear}>Clear</button>
            </div>

          </div>

          {/* Results */}
          {generated && (
            <>
              <div className="mb-2 text-muted" style={{ fontSize: "13px" }}>
                Total Records: {rows.length}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered table-hover table-sm" style={{ fontSize: "12px", minWidth: "1050px" }}>
                  <thead className="table-success">
                    <tr>
                      <th>#</th>
                      <th>Deal ID</th>
                      <th>Deal Date</th>
                      <th>Buyer</th>
                      <th>Shipper</th>
                      <th>Container No</th>
                      <th>Rec Date</th>
                      <th>Net Weight</th>
                      <th>Ship Line</th>
                      <th>ETD</th>
                      <th>ETA</th>
                      <th>BL No</th>
                      <th>BL Date</th>
                      <th>Invoice No</th>
                      <th>Invoice Date</th>
                      <th>Invoice Amt</th>
                      <th>Courier Date</th>
                      <th>Ref No</th>
                      <th>Doc Rec Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={19} className="text-center text-muted py-3">No records found.</td></tr>
                    ) : rows.map((r, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{r.DealID}</td>
                        <td>{fmt(r.DealDate)}</td>
                        <td>{r.BuyerName || "-"}</td>
                        <td>{r.ShipperName || "-"}</td>
                        <td>{r.ContainerNo || "-"}</td>
                        <td>{fmt(r.RecDate)}</td>
                        <td>{r.NetWeight ?? "-"}</td>
                        <td>{r.ShipLine || "-"}</td>
                        <td>{fmt(r.ETD)}</td>
                        <td>{fmt(r.ETA)}</td>
                        <td>{r.BLNo || "-"}</td>
                        <td>{fmt(r.BLDate)}</td>
                        <td>{r.InvoiceNo || "-"}</td>
                        <td>{fmt(r.InvoiceDate)}</td>
                        <td>{r.InvoiceAmount ?? "-"}</td>
                        <td>{fmt(r.CourierDate)}</td>
                        <td>{r.RefNo || "-"}</td>
                        <td>{fmt(r.DocRecDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default ShipmentScheduleReport;
